// ============================================================================
// BrightPath - NotificationItem Component
// BP-12-03: Notifications Frontend
// ============================================================================

import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  Calendar,
  Heart,
  FileText,
  Users,
  Scale,
  ShoppingCart,
  Settings,
  X,
  Check,
  AlertCircle,
} from 'lucide-react';
import type {
  NotificationItemProps,
  NotificationCategory,
  NotificationPriority,
} from '../types/notificationsTypes';
import {
  NOTIFICATION_CATEGORY_CONFIG,
  NOTIFICATION_PRIORITY_CONFIG,
} from '../types/notificationsTypes';

// ----------------------------------------------------------------------------
// Icon Mapping
// ----------------------------------------------------------------------------

const categoryIcons: Record<NotificationCategory, React.ElementType> = {
  appointment: Calendar,
  health: Heart,
  document: FileText,
  community: Users,
  legal: Scale,
  marketplace: ShoppingCart,
  system: Settings,
};

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkRead,
  onDelete,
  onClick,
  compact = false,
}) => {
  const {
    id,
    title,
    body,
    category,
    priority,
    isRead,
    createdAt,
    actionUrl,
  } = notification;

  const categoryConfig = NOTIFICATION_CATEGORY_CONFIG[category];
  const priorityConfig = NOTIFICATION_PRIORITY_CONFIG[priority];
  const Icon = categoryIcons[category] || Settings;

  const timeAgo = formatDistanceToNow(new Date(createdAt), { addSuffix: true });

  // Handle click
  const handleClick = () => {
    if (!isRead && onMarkRead) {
      onMarkRead(id);
    }
    onClick?.(notification);
  };

  // Handle mark as read
  const handleMarkRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkRead?.(id);
  };

  // Handle delete
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(id);
  };

  // Color classes based on category
  const getIconBgClass = () => {
    switch (category) {
      case 'appointment':
        return 'bg-sage/15 text-sage-dark';
      case 'health':
        return 'bg-terracotta/15 text-terracotta';
      case 'document':
        return 'bg-navy/10 text-navy';
      case 'community':
        return 'bg-blue-100 text-blue-600';
      case 'legal':
        return 'bg-navy/15 text-navy';
      case 'marketplace':
        return 'bg-terracotta/10 text-terracotta';
      case 'system':
        return 'bg-warm-gray/15 text-warm-gray';
      default:
        return 'bg-cream-dark text-navy';
    }
  };

  // Compact variant
  if (compact) {
    return (
      <div
        onClick={handleClick}
        className={`
          flex items-start gap-3 p-3 rounded-lg cursor-pointer
          transition-colors duration-200
          ${isRead ? 'bg-white' : 'bg-sage/5'}
          hover:bg-cream-dark
        `}
      >
        <div className={`p-2 rounded-lg ${getIconBgClass()}`}>
          <Icon className="w-4 h-4" />
        </div>
        
        <div className="flex-1 min-w-0">
          <p className={`text-sm truncate ${isRead ? 'text-warm-gray' : 'text-navy font-medium'}`}>
            {title}
          </p>
          <p className="text-xs text-warm-gray mt-0.5">{timeAgo}</p>
        </div>

        {!isRead && (
          <div className="w-2 h-2 rounded-full bg-sage flex-shrink-0 mt-2" />
        )}
      </div>
    );
  }

  // Full variant
  return (
    <div
      onClick={handleClick}
      className={`
        group relative flex gap-4 p-4 rounded-xl border
        transition-all duration-200 cursor-pointer
        ${isRead 
          ? 'bg-white border-warm-gray/10 hover:border-warm-gray/20' 
          : 'bg-sage/5 border-sage/20 hover:border-sage/30'
        }
        ${priority === 'urgent' ? 'border-l-4 border-l-red-500' : ''}
        ${priority === 'high' ? 'border-l-4 border-l-terracotta' : ''}
      `}
    >
      {/* Icon */}
      <div className={`p-3 rounded-xl ${getIconBgClass()} flex-shrink-0`}>
        <Icon className="w-5 h-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h4 className={`text-sm ${isRead ? 'text-navy-light' : 'text-navy font-semibold'}`}>
            {title}
          </h4>
          
          {/* Priority badge for high/urgent */}
          {(priority === 'high' || priority === 'urgent') && (
            <span className={`
              px-2 py-0.5 rounded-full text-xs font-medium
              ${priorityConfig.bgColor} ${priorityConfig.color}
            `}>
              {priority === 'urgent' && <AlertCircle className="w-3 h-3 inline mr-1" />}
              {priorityConfig.label}
            </span>
          )}
        </div>

        <p className={`text-sm mt-1 ${isRead ? 'text-warm-gray' : 'text-navy-light'}`}>
          {body}
        </p>

        <div className="flex items-center gap-3 mt-2">
          <span className="text-xs text-warm-gray">{timeAgo}</span>
          <span className="text-xs text-warm-gray">•</span>
          <span className="text-xs text-warm-gray capitalize">{categoryConfig.label}</span>
        </div>
      </div>

      {/* Actions (visible on hover) */}
      <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {!isRead && onMarkRead && (
          <button
            onClick={handleMarkRead}
            className="p-1.5 rounded-lg hover:bg-cream-dark text-warm-gray hover:text-sage transition-colors"
            title="Mark as read"
          >
            <Check className="w-4 h-4" />
          </button>
        )}
        
        {onDelete && (
          <button
            onClick={handleDelete}
            className="p-1.5 rounded-lg hover:bg-cream-dark text-warm-gray hover:text-red-500 transition-colors"
            title="Delete"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Unread indicator */}
      {!isRead && (
        <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-sage group-hover:opacity-0 transition-opacity" />
      )}
    </div>
  );
};

export default NotificationItem;


